import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useToast } from '../../../components/ui/use-toast';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { useTenantContext } from '../../../context/TenantContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import {
  Mail,
  Plus,
  Key,
  Trash2,
  Loader2,
  RefreshCw,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  AlertTriangle,
  HardDrive,
  Search,
  Shuffle,
} from 'lucide-react';
import {
  listEmails,
  listDomains,
  createEmail,
  updatePassword,
  deleteEmail,
  generateSecurePassword,
  getWebmailUrl,
  formatBytes,
  type CpanelEmail,
} from '../../../services/cpanelEmailService';
import { supabase } from '../../../lib/supabase';

export const EmailManagement = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const { tenant } = useTenantContext();

  // États
  const [emails, setEmails] = useState<CpanelEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Domaines disponibles
  const [availableDomains, setAvailableDomains] = useState<string[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [loadingDomains, setLoadingDomains] = useState(true);
  
  // Domaine actuel (fallback)
  const currentDomain = selectedDomain || window.location.hostname;
  
  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<CpanelEmail | null>(null);
  
  // Formulaires
  const [newEmail, setNewEmail] = useState({ username: '', password: '', quota: '500' });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // États de chargement
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Charger les domaines disponibles
  const fetchDomains = async () => {
    setLoadingDomains(true);
    try {
      const result = await listDomains();
      const hostname = window.location.hostname;
      
      if (result.success && result.data) {
        // Vérifier si l'utilisateur est super_admin
        const { data: userData } = await supabase
          .from('users')
          .select('role')
          .eq('id', (await supabase.auth.getUser()).data.user?.id)
          .single();
        
        const isSuperAdmin = userData?.role === 'super_admin';
        
        if (isSuperAdmin) {
          // Super admin voit tous les domaines
          setAvailableDomains(result.data);
          if (result.data.includes(hostname)) {
            setSelectedDomain(hostname);
          } else if (result.data.length > 0) {
            setSelectedDomain(result.data[0]);
          }
        } else {
          // Admin normal voit uniquement son domaine actuel
          if (result.data.includes(hostname)) {
            setAvailableDomains([hostname]);
            setSelectedDomain(hostname);
          } else {
            // Si le domaine actuel n'est pas dans cPanel, afficher un message
            setAvailableDomains([]);
            setSelectedDomain(hostname);
            toast({
              title: 'Domaine non configuré',
              description: `Le domaine ${hostname} n'est pas configuré dans cPanel`,
              variant: 'destructive',
            });
          }
        }
      } else {
        console.error('Erreur lors du chargement des domaines:', result.error);
        // Fallback sur le hostname actuel
        setAvailableDomains([hostname]);
        setSelectedDomain(hostname);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des domaines:', error);
      const hostname = window.location.hostname;
      setAvailableDomains([hostname]);
      setSelectedDomain(hostname);
    } finally {
      setLoadingDomains(false);
    }
  };

  // Charger les emails
  const fetchEmails = async () => {
    if (!currentDomain) return;
    
    setLoading(true);
    try {
      const result = await listEmails(currentDomain);
      
      if (result.success && result.data) {
        setEmails(result.data);
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Impossible de charger les emails',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des emails:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur de connexion au serveur',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Charger les domaines au démarrage
  useEffect(() => {
    fetchDomains();
  }, []);

  // Charger les emails quand le domaine change
  useEffect(() => {
    if (selectedDomain) {
      fetchEmails();
    }
  }, [selectedDomain]);

  // Filtrer les emails
  const filteredEmails = emails.filter(email =>
    email.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    email.user.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Créer un email
  const handleCreateEmail = async () => {
    if (!newEmail.username || !newEmail.password) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      const fullEmail = `${newEmail.username}@${currentDomain}`;
      const quota = newEmail.quota === 'unlimited' ? 'unlimited' : parseInt(newEmail.quota);
      
      const result = await createEmail({
        email: fullEmail,
        password: newEmail.password,
        domain: currentDomain,
        quota,
      });

      if (result.success) {
        toast({
          title: 'Succès',
          description: `L'adresse ${fullEmail} a été créée`,
        });
        setCreateModalOpen(false);
        setNewEmail({ username: '', password: '', quota: '500' });
        fetchEmails();
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Impossible de créer l\'email',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de la création',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Changer le mot de passe
  const handleUpdatePassword = async () => {
    if (!selectedEmail || !newPassword) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer un nouveau mot de passe',
        variant: 'destructive',
      });
      return;
    }

    setIsUpdating(true);
    try {
      const result = await updatePassword({
        email: selectedEmail.email,
        password: newPassword,
        domain: currentDomain,
      });

      if (result.success) {
        toast({
          title: 'Succès',
          description: `Le mot de passe de ${selectedEmail.email} a été modifié`,
        });
        setPasswordModalOpen(false);
        setNewPassword('');
        setSelectedEmail(null);
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Impossible de modifier le mot de passe',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de la modification',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Supprimer un email
  const handleDeleteEmail = async () => {
    if (!selectedEmail) return;

    setIsDeleting(true);
    try {
      const result = await deleteEmail({
        email: selectedEmail.email,
        domain: currentDomain,
      });

      if (result.success) {
        toast({
          title: 'Succès',
          description: `L'adresse ${selectedEmail.email} a été supprimée`,
        });
        setDeleteModalOpen(false);
        setSelectedEmail(null);
        fetchEmails();
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Impossible de supprimer l\'email',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de la suppression',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Copier dans le presse-papier
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copié',
      description: `${label} copié dans le presse-papier`,
    });
  };

  // Générer un mot de passe
  const handleGeneratePassword = (setter: (value: string) => void) => {
    const password = generateSecurePassword(16);
    setter(password);
  };

  // Ouvrir le webmail
  const openWebmail = () => {
    window.open(getWebmailUrl(currentDomain), '_blank');
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Gestion des Emails</h1>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">Domaine :</span>
              {loadingDomains ? (
                <Loader2 className="h-4 w-4 text-yellow-400 animate-spin" />
              ) : availableDomains.length > 1 ? (
                <select
                  value={selectedDomain}
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  className="bg-white/10 border border-white/20 text-yellow-400 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                >
                  {availableDomains.map((domain) => (
                    <option key={domain} value={domain} className="bg-gray-900 text-white">
                      {domain}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-yellow-400">{currentDomain}</span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openWebmail}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Webmail
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEmails}
              disabled={loading}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              onClick={() => setCreateModalOpen(true)}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:from-yellow-500 hover:to-yellow-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvel Email
            </Button>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent"
              placeholder="Rechercher une adresse email..."
            />
          </div>
        </div>

        {/* Liste des emails */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="p-12 text-center">
              <Mail className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                {searchQuery ? 'Aucun résultat' : 'Aucune adresse email'}
              </h3>
              <p className="text-gray-400">
                {searchQuery
                  ? 'Aucune adresse ne correspond à votre recherche'
                  : 'Créez votre première adresse email pour ce domaine'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Adresse Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Quota
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Utilisation
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredEmails.map((email, index) => (
                    <tr key={email.email} className={`hover:bg-white/5 ${index % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-yellow-400/10 rounded-full flex items-center justify-center">
                            <Mail className="h-5 w-5 text-yellow-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white flex items-center gap-2">
                              {email.email}
                              <button
                                onClick={() => copyToClipboard(email.email, 'Email')}
                                className="text-gray-400 hover:text-white"
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <div className="text-sm text-gray-400">
                              Utilisateur: {email.user}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <HardDrive className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-white">
                            {email.diskquota === 'unlimited' || email._diskquota === 0
                              ? 'Illimité'
                              : `${email._diskquota} MB`}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-white">{email.humandiskused}</span>
                          {email._diskquota > 0 && (
                            <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  email.diskusedpercent > 90
                                    ? 'bg-red-500'
                                    : email.diskusedpercent > 70
                                    ? 'bg-yellow-500'
                                    : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(100, email.diskusedpercent)}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {email.has_suspended ? (
                          <Badge className="bg-red-400/20 text-red-400 border-red-400/20">
                            Suspendu
                          </Badge>
                        ) : (
                          <Badge className="bg-green-400/20 text-green-400 border-green-400/20">
                            Actif
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedEmail(email);
                              setPasswordModalOpen(true);
                            }}
                            className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10"
                            title="Changer le mot de passe"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedEmail(email);
                              setDeleteModalOpen(true);
                            }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-400/10 rounded-lg">
                <Mail className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Emails</p>
                <p className="text-xl font-bold text-white">{emails.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-400/10 rounded-lg">
                <HardDrive className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Espace Utilisé</p>
                <p className="text-xl font-bold text-white">
                  {formatBytes(emails.reduce((acc, e) => acc + e._diskused, 0))}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-400/10 rounded-lg">
                <ExternalLink className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Accès Webmail</p>
                <button
                  type="button"
                  onClick={openWebmail}
                  className="text-left text-sm font-medium text-blue-300 hover:text-blue-200 truncate underline-offset-2 hover:underline"
                >
                  {getWebmailUrl(currentDomain)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Création */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Mail className="h-5 w-5 text-yellow-400" />
              Créer une adresse email
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Créez une nouvelle adresse email pour {currentDomain}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Nom d'utilisateur */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Adresse email *
              </label>
              <div className="flex">
                <Input
                  type="text"
                  value={newEmail.username}
                  onChange={(e) => setNewEmail({ ...newEmail, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                  className="flex-1 bg-white/5 border border-white/10 text-white rounded-l-xl rounded-r-none"
                  placeholder="contact"
                />
                <span className="inline-flex items-center px-4 bg-white/10 border border-l-0 border-white/10 rounded-r-xl text-gray-400">
                  @{currentDomain}
                </span>
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mot de passe *
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newEmail.password}
                    onChange={(e) => setNewEmail({ ...newEmail, password: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl pr-10"
                    placeholder="Minimum 8 caractères"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleGeneratePassword((p) => setNewEmail({ ...newEmail, password: p }))}
                  className="border-white/20 text-white hover:bg-white/10"
                  title="Générer un mot de passe"
                >
                  <Shuffle className="h-4 w-4" />
                </Button>
              </div>
              {newEmail.password && (
                <button
                  onClick={() => copyToClipboard(newEmail.password, 'Mot de passe')}
                  className="mt-2 text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" />
                  Copier le mot de passe
                </button>
              )}
            </div>

            {/* Quota */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Quota (MB)
              </label>
              <select
                value={newEmail.quota}
                onChange={(e) => setNewEmail({ ...newEmail, quota: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              >
                <option value="100">100 MB</option>
                <option value="250">250 MB</option>
                <option value="500">500 MB</option>
                <option value="1000">1 GB</option>
                <option value="2000">2 GB</option>
                <option value="5000">5 GB</option>
                <option value="unlimited">Illimité</option>
              </select>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setCreateModalOpen(false)}
              disabled={isCreating}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateEmail}
              disabled={isCreating || !newEmail.username || !newEmail.password}
              className="bg-yellow-400 text-black hover:bg-yellow-500"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Mot de passe */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Key className="h-5 w-5 text-yellow-400" />
              Changer le mot de passe
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Modifier le mot de passe de {selectedEmail?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nouveau mot de passe *
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl pr-10"
                    placeholder="Minimum 8 caractères"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleGeneratePassword(setNewPassword)}
                  className="border-white/20 text-white hover:bg-white/10"
                  title="Générer un mot de passe"
                >
                  <Shuffle className="h-4 w-4" />
                </Button>
              </div>
              {newPassword && (
                <button
                  onClick={() => copyToClipboard(newPassword, 'Mot de passe')}
                  className="mt-2 text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" />
                  Copier le mot de passe
                </button>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setPasswordModalOpen(false);
                setNewPassword('');
                setSelectedEmail(null);
              }}
              disabled={isUpdating}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button
              onClick={handleUpdatePassword}
              disabled={isUpdating || !newPassword || newPassword.length < 8}
              className="bg-yellow-400 text-black hover:bg-yellow-500"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Modification...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Modifier
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Suppression */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Cette action est irréversible. Tous les emails seront perdus.
            </DialogDescription>
          </DialogHeader>

          {selectedEmail && (
            <div className="mt-4 bg-red-400/10 rounded-xl p-4 border border-red-400/20">
              <p className="text-sm text-gray-300 mb-1">
                <span className="font-medium text-white">Adresse :</span> {selectedEmail.email}
              </p>
              <p className="text-sm text-gray-300">
                <span className="font-medium text-white">Espace utilisé :</span> {selectedEmail.humandiskused}
              </p>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedEmail(null);
              }}
              disabled={isDeleting}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteEmail}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default EmailManagement;
